// src/pages/DashboardAdminPage.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient.js';

import DashboardSidebarAdmin from '../components/Dashboard/DashboardSidebarAdmin.jsx';
import DashboardNavbarAdmin from '../components/Dashboard/DashboardNavbarAdmin.jsx';


const DashboardAdminPage = () => {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalToday: 0,
    processing: 0,
    success: 0,
    failed: 0
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const [timeFilter, setTimeFilter] = useState('today'); // 'today', 'week', 'month', 'all'

  // Global Credit Stats
  const [globalStats, setGlobalStats] = useState({
    totalProviderCredits: 0,
    totalUserCredits: 0,
    totalCreditsConsumed: 0
  });
  const [globalStatsLoading, setGlobalStatsLoading] = useState(true);

  // Detail view states
  const [limit, setLimit] = useState(10); // Default 10 rows
  const [detailData, setDetailData] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailFilters, setDetailFilters] = useState({
    provider: 'all',
    model: 'all',
    status: 'all'
  });

  // Pagination State
  const [page, setPage] = useState(1);
  const [totalDetailCount, setTotalDetailCount] = useState(0);

  // Error Modal States
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [selectedError, setSelectedError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Cek user / session
        const { data, error: authError } = await supabase.auth.getUser();
        const authUser = data?.user;

        if (authError || !authUser) {
          navigate('/login');
          return;
        }
        setUser(authUser);

        // Ambil profile (harus admin)
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', authUser.id)
          .single();

        if (profileError && profileError.code !== 'PGRST116') {
          throw profileError;
        }

        if (!profileData || !profileData.phone) {
          navigate('/login');
          return;
        }

        if (profileData.role !== 'admin') {
          // Bukan admin → lempar ke dashboard user biasa
          navigate('/dashboard');
          return;
        }

        setProfile(profileData);
      } catch (err) {
        console.error('Error fetching admin dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  // Fetch Global Stats (Provider, Users, Consumed)
  const fetchGlobalStats = async (isBackground = false) => {
    try {
      if (!isBackground) setGlobalStatsLoading(true);

      // 1. Total Provider Credits
      const { data: providers, error: errProviders } = await supabase
        .from('providers')
        .select('credit_balance');

      if (errProviders) {
        if (errProviders.code !== '42P01') console.error('Error fetching providers:', errProviders);
      }

      // 2. Total User Credits
      const { data: profiles, error: errProfiles } = await supabase
        .from('profiles')
        .select('credit_balance');

      if (errProfiles) console.error('Error fetching profiles:', errProfiles);

      // 3. Total Credits Consumed
      // EXCLUDE STATUS 'failed' or 'error'
      const { data: runs, error: errRuns } = await supabase
        .from('ai_model_runs')
        .select('credits_charged, status');

      if (errRuns) console.error('Error fetching runs:', errRuns);

      // Calculate Sums
      const totalProviderCredits = providers?.reduce((acc, curr) => acc + (curr.credit_balance || 0), 0) || 0;
      const totalUserCredits = profiles?.reduce((acc, curr) => acc + (curr.credit_balance || 0), 0) || 0;

      // Filter out failed/error runs for consumption stats
      const validRuns = runs?.filter(r => r.status !== 'failed' && r.status !== 'error') || [];
      const totalCreditsConsumed = validRuns.reduce((acc, curr) => acc + (curr.credits_charged || 0), 0);

      setGlobalStats({
        totalProviderCredits,
        totalUserCredits,
        totalCreditsConsumed
      });

    } catch (err) {
      console.error("Error fetching global stats:", err);
    } finally {
      if (!isBackground) setGlobalStatsLoading(false);
    }
  };

  useEffect(() => {
    fetchGlobalStats();
  }, []);

  // Fetch stats (Total, Processing, Success, Failed)
  const fetchTodayStats = async (isBackground = false) => {
    try {
      if (!isBackground) setStatsLoading(true);

      // Calculate date range based on filter
      let startDate = new Date();
      if (timeFilter === 'today') {
        startDate.setHours(0, 0, 0, 0);
      } else if (timeFilter === 'week') {
        startDate.setDate(startDate.getDate() - 7);
        startDate.setHours(0, 0, 0, 0);
      } else if (timeFilter === 'month') {
        startDate.setMonth(startDate.getMonth() - 1);
        startDate.setHours(0, 0, 0, 0);
      } else if (timeFilter === 'all') {
        startDate = new Date('2020-01-01'); // Far past date for "all time"
      }
      const startDateISO = startDate.toISOString();

      // Fetch filtered runs (Total, Success, Failed)
      const { data, error } = await supabase
        .from('ai_model_runs')
        .select('status')
        .gte('created_at', startDateISO);

      if (error) throw error;

      // Fetch ALL processing jobs (not filtered by date to ensure we track all active jobs)
      const { data: processingData, error: processingError } = await supabase
        .from('ai_model_runs')
        .select('status')
        .eq('status', 'processing');

      if (processingError) throw processingError;

      // Calculate stats
      const totalToday = data?.length || 0;
      const processing = processingData?.length || 0; // All processing jobs
      const success = data?.filter(r => r.status === 'success').length || 0;
      const failed = data?.filter(r => r.status === 'error' || r.status === 'failed').length || 0;

      setStats({ totalToday, processing, success, failed });
    } catch (error) {
      console.error('Error fetching today stats:', error);
    } finally {
      if (!isBackground) setStatsLoading(false);
    }
  };

  useEffect(() => {
    fetchTodayStats();
  }, [timeFilter]);


  // Fetch detail data (lazy load)
  // Added isBackground param to prevent loading spinner during silent updates
  const fetchDetailData = async (customFilters = null, isBackground = false) => {
    try {
      if (!isBackground) setDetailLoading(true);

      // Use custom filters if provided, otherwise use state
      const filters = customFilters || detailFilters;

      // Calculate date range (same as stats)
      let startDate = new Date();
      if (timeFilter === 'today') {
        startDate.setHours(0, 0, 0, 0);
      } else if (timeFilter === 'week') {
        startDate.setDate(startDate.getDate() - 7);
        startDate.setHours(0, 0, 0, 0);
      } else if (timeFilter === 'month') {
        startDate.setMonth(startDate.getMonth() - 1);
        startDate.setHours(0, 0, 0, 0);
      } else if (timeFilter === 'all') {
        startDate = new Date('2020-01-01');
      }

      let query = supabase
        .from('ai_model_runs')
        .select('id, created_at, user_id, feature_code, model, provider, status, error_message, profiles(username, email)', { count: 'exact' })
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: false });

      if (limit !== 'all') {
        const from = (page - 1) * limit;
        const to = from + limit - 1;
        query = query.range(from, to);
      } else {
        query = query.limit(1000); // Safety cap for 'all'
      }

      // Apply filters
      if (filters.provider !== 'all') {
        query = query.eq('provider', filters.provider);
      }
      if (filters.model !== 'all') {
        query = query.eq('model', filters.model);
      }
      if (filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }

      const { data, count, error } = await query;
      if (error) throw error;

      setTotalDetailCount(count || 0);

      // Only update if data is different or it's a background refresh (which usually means we want latest)
      // For simplicity/robustness, we just set the data. React handles diffing well.
      setDetailData(data || []);
    } catch (error) {
      console.error('Error fetching detail data:', error);
    } finally {
      if (!isBackground) setDetailLoading(false);
    }
  };

  // Initial Fetch & Refetch when filters change
  useEffect(() => {
    fetchDetailData();
  }, [detailFilters, timeFilter, limit, page]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [detailFilters, timeFilter, limit]);

  // --- Realtime & Interval Logic ---
  useEffect(() => {
    // 1. Set up Interval for periodic silent refresh (every 5 seconds)
    //    This ensures "processing" items eventually update and new items appear even if realtime misses.
    const interval = setInterval(() => {
      fetchTodayStats(true); // Silent
      fetchDetailData(null, true); // Silent
      fetchGlobalStats(true); // Silent Realtime Credit
    }, 5000);

    // 2. Realtime Subscription
    //    We use this to trigger an *immediate* silent refresh when DB changes.
    const channel = supabase.channel('admin_dashboard_realtime')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'ai_model_runs' },
        (payload) => {
          // Trigger silent refresh immediately
          fetchTodayStats(true); // Silent
          fetchGlobalStats(true); // Silent Realtime Credit

          if (payload.eventType === 'INSERT') {
            // For new rows, we definitely need to fetch to get the joined profile data
            fetchDetailData(null, true);
          } else if (payload.eventType === 'UPDATE') {
            // For updates, we can either optimistic update OR just fetch. 
            // Fetching is safer for consistency with the interval logic.
            // We can also double-up: optimistic update specific row AND fetch.

            // Optimistic Update for responsiveness
            setDetailData(prev => prev.map(item => {
              if (String(item.id) === String(payload.new.id)) {
                return { ...item, ...payload.new, profiles: item.profiles };
              }
              return item;
            }));

            // ... then fetch to be sure (in case of complex changes)
            fetchDetailData(null, true);
          }
        }
      )
      .subscribe();


    return () => {
      clearInterval(interval);
      supabase.removeChannel(channel);
    };
  }, [detailFilters, timeFilter, limit, page]); // Re-subscribe if main config changes

  if (loading) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-slate-50">
        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mb-3" />
        <p className="text-slate-500">Memuat admin dashboard...</p>
      </div>
    );
  }

  const handleUpdateStatus = async (id, newStatus) => {
    if (!window.confirm(`Are you sure you want to mark this job as ${newStatus}?`)) return;

    try {
      const { error } = await supabase
        .from('ai_model_runs')
        .update({ status: newStatus })
        .eq('id', id);

      if (error) throw error;

      // Update local state immediately for responsiveness
      setDetailData(prev => prev.map(item =>
        item.id === id ? { ...item, status: newStatus } : item
      ));

      // Refresh stats
      fetchTodayStats();

    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status: ' + error.message);
    }
  };

  const displayName =
    profile?.username || user?.email?.split('@')[0] || 'Admin';

  return (
    <div className="h-screen overflow-hidden bg-slate-50 font-sans">
      <div className="flex h-full min-w-0">
        {/* SIDEBAR ADMIN (responsive: overlay di mobile, fixed di desktop) */}
        <DashboardSidebarAdmin />

        {/* AREA KANAN */}
        <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
          {/* NAVBAR ADMIN */}
          <DashboardNavbarAdmin displayName={displayName} />

          {/* KONTEN ADMIN */}
          <main className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="max-w-7xl mx-auto space-y-3">

              {/* Provider Balance Health (Compact Style) */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-3 relative overflow-hidden">
                <div className="flex items-center justify-between gap-4 mb-2">
                  <div className="flex items-center gap-3">
                    {/* Low Balance Indicator */}
                    {(globalStats.totalProviderCredits < globalStats.totalUserCredits * 0.1) && !globalStatsLoading && (
                      <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-600 text-[10px] font-bold animate-pulse">
                        LOW
                      </span>
                    )}
                    {/* Total Kredit Badge */}
                    <div className="flex items-center gap-1.5 px-2 py-0.5 bg-slate-100 rounded-full border border-slate-200">
                      <span className="text-[10px] font-bold text-slate-500 uppercase">Total Kredit:</span>
                      <span className="text-[11px] font-bold text-slate-700 font-mono">{globalStats.totalUserCredits.toLocaleString('id-ID')}</span>
                    </div>
                  </div>
                </div>

                {/* The "Fuel Tank" Bar - COMPACT HEIGHT */}
                <div className="relative w-full h-4 bg-slate-50 rounded-md overflow-hidden flex shadow-inner border border-slate-200 ring-1 ring-slate-100">

                  {/* CONDITION: Critical (Used > Limit) -> Show Full RED Bar */}
                  {globalStats.totalProviderCredits < globalStats.totalCreditsConsumed ? (
                    <div
                      className="h-full bg-red-500 flex items-center justify-center relative group transition-all duration-1000 w-full"
                    >
                      <span className="text-white text-xs font-bold drop-shadow-md">Habis</span>
                      <div className="absolute opacity-0 group-hover:opacity-100 bottom-full mb-2 bg-slate-900 text-white text-xs py-1 px-2 rounded whitespace-nowrap pointer-events-none transition-opacity z-10">
                        Warning: Usage ({globalStats.totalCreditsConsumed.toLocaleString('id-ID')}) exceeds Balance ({globalStats.totalProviderCredits.toLocaleString('id-ID')})
                      </div>
                    </div>
                  ) : (
                    /* CONDITION: Healthy (Limit > Used) -> Show Proportional Segments based on LIMIT */
                    <>
                      {/* Segment 1: Remaining (Green) = Total - Used */}
                      <div
                        className="h-full bg-emerald-500 flex items-center justify-center relative group transition-all duration-1000 border-r border-white/10"
                        style={{
                          width: globalStatsLoading ? '0%' : `${Math.max(((globalStats.totalProviderCredits - globalStats.totalCreditsConsumed) / Math.max(globalStats.totalProviderCredits, 1) * 100), 0)}%`
                        }}
                      >
                        {/* Text Inside Bar if wide enough */}
                        {!globalStatsLoading && ((globalStats.totalProviderCredits - globalStats.totalCreditsConsumed) / globalStats.totalProviderCredits) > 0.05 && (
                          <span className="text-white text-xs font-bold drop-shadow-md">Kredit Provider</span>
                        )}
                        <div className="absolute opacity-0 group-hover:opacity-100 bottom-full mb-2 bg-slate-900 text-white text-xs py-1 px-2 rounded whitespace-nowrap pointer-events-none transition-opacity z-10">
                          Remaining: {(globalStats.totalProviderCredits - globalStats.totalCreditsConsumed).toLocaleString('id-ID')}
                        </div>
                      </div>

                      {/* Segment 2: Used (Gray) = Used */}
                      <div
                        className="h-full bg-slate-300 flex items-center justify-center relative group transition-all duration-1000"
                        style={{
                          width: globalStatsLoading ? '0%' : `${Math.min((globalStats.totalCreditsConsumed / Math.max(globalStats.totalProviderCredits, 1) * 100), 100)}%`
                        }}
                      >
                        <div className="absolute opacity-0 group-hover:opacity-100 bottom-full mb-2 bg-slate-800 text-white text-xs py-1 px-2 rounded whitespace-nowrap pointer-events-none transition-opacity z-10">
                          Terpakai: {globalStats.totalCreditsConsumed.toLocaleString('id-ID')} ({(globalStats.totalCreditsConsumed / Math.max(globalStats.totalProviderCredits, 1) * 100).toFixed(1)}%)
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Legends / Stats Details (Sisa Left, Hangus Right) COMPACT */}
                <div className="mt-2 grid grid-cols-2 gap-4 border-t border-slate-50 pt-2">
                  {/* Reserve (Left) */}
                  <div className="text-left">
                    <div className="flex items-center gap-2 justify-start mb-0.5">
                      <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                      <span className="text-[10px] font-bold uppercase text-emerald-600">Provider</span>
                    </div>
                    <p className="text-sm font-bold font-mono text-emerald-600">
                      {globalStats.totalProviderCredits.toLocaleString('id-ID')}
                    </p>
                  </div>

                  {/* Consumed (Right) */}
                  <div className="text-right">
                    <div className="flex items-center gap-2 justify-end mb-0.5">
                      <span className="text-[10px] font-bold text-slate-500 uppercase">Berjalan</span>
                      <span className="w-2 h-2 rounded-full bg-slate-300"></span>
                    </div>
                    <p className="text-sm font-bold text-slate-700 font-mono">
                      {globalStats.totalCreditsConsumed.toLocaleString('id-ID')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Filter Period COMPACT */}
              <div className="flex items-center justify-between bg-white rounded-lg shadow-sm border border-slate-200 px-3 py-2">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                  </svg>
                  <span className="text-xs font-semibold text-slate-700">Filter Period:</span>
                </div>
                <select
                  value={timeFilter}
                  onChange={(e) => setTimeFilter(e.target.value)}
                  className="px-2 py-1 rounded border border-slate-200 bg-white text-xs font-medium text-slate-700 hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer transition-all"
                >
                  <option value="today">Hari Ini</option>
                  <option value="week">7 Hari Terakhir</option>
                  <option value="month">30 Hari Terakhir</option>
                  <option value="all">Semua Waktu</option>
                </select>
              </div>

              {/* AI Generation Stats - Today ULTRA COMPACT */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                {/* Total Today */}
                <div className="bg-white rounded border border-slate-200 p-2 flex items-center justify-between hover:shadow-sm transition-shadow">
                  <div>
                    <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-0.5">
                      {timeFilter === 'today' ? 'Hari Ini' : 'Total'}
                    </p>
                    {statsLoading ? (
                      <div className="h-5 w-12 bg-slate-100 animate-pulse rounded" />
                    ) : (
                      <p className="text-lg font-bold text-slate-900 leading-none">{stats.totalToday.toLocaleString('id-ID')}</p>
                    )}
                  </div>
                  <div className="w-8 h-8 rounded bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-sm">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                </div>

                {/* Processing */}
                <div className="bg-amber-50 rounded border border-amber-200 p-2 flex items-center justify-between hover:shadow-sm transition-shadow">
                  <div>
                    <p className="text-[10px] font-semibold text-amber-700 uppercase tracking-wider mb-0.5">Processing</p>
                    {statsLoading ? (
                      <div className="h-5 w-12 bg-amber-200 animate-pulse rounded" />
                    ) : (
                      <p className="text-lg font-bold text-amber-800 leading-none">{stats.processing.toLocaleString('id-ID')}</p>
                    )}
                  </div>
                  <div className="w-8 h-8 rounded bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-sm">
                    <svg className={`w-4 h-4 text-white ${stats.processing > 0 ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                </div>

                {/* Success */}
                <div className="bg-emerald-50 rounded border border-emerald-200 p-2 flex items-center justify-between hover:shadow-sm transition-shadow">
                  <div>
                    <p className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wider mb-0.5">Success</p>
                    {statsLoading ? (
                      <div className="h-5 w-12 bg-emerald-200 animate-pulse rounded" />
                    ) : (
                      <p className="text-lg font-bold text-emerald-800 leading-none">{stats.success.toLocaleString('id-ID')}</p>
                    )}
                  </div>
                  <div className="w-8 h-8 rounded bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-sm">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>

                {/* Failed */}
                <div className="bg-rose-50 rounded border border-rose-200 p-2 flex items-center justify-between hover:shadow-sm transition-shadow">
                  <div>
                    <p className="text-[10px] font-semibold text-rose-700 uppercase tracking-wider mb-0.5">Failed</p>
                    {statsLoading ? (
                      <div className="h-5 w-12 bg-rose-200 animate-pulse rounded" />
                    ) : (
                      <p className="text-lg font-bold text-rose-800 leading-none">{stats.failed.toLocaleString('id-ID')}</p>
                    )}
                  </div>
                  <div className="w-8 h-8 rounded bg-gradient-to-br from-rose-500 to-red-600 flex items-center justify-center shadow-sm">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                </div>
              </div>



              {/* Detail Data Section - Collapsible - COMPACT */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                {/* Header - Clickable to expand/collapse */}
                <div className="px-4 py-2 border-b border-slate-200">
                  <h3 className="text-xs font-semibold text-slate-700">
                    Detail Data {detailData.length > 0 && `(${detailData.length})`}
                  </h3>
                </div>

                {/* Content */}
                <div>
                  {/* Filters COMPACT */}
                  <div className="px-4 py-2 bg-slate-50 border-b border-slate-200">
                    <div className="flex flex-wrap gap-2">
                      <select
                        value={detailFilters.provider}
                        onChange={(e) => setDetailFilters({ ...detailFilters, provider: e.target.value })}
                        className="px-2 py-1.5 text-[11px] rounded border border-slate-200 bg-white hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                      >
                        <option value="all">All Providers</option>
                        {[...new Set(detailData.map(d => d.provider))].filter(Boolean).sort().map(p => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                      </select>

                      <select
                        value={detailFilters.model}
                        onChange={(e) => setDetailFilters({ ...detailFilters, model: e.target.value })}
                        className="px-2 py-1.5 text-[11px] rounded border border-slate-200 bg-white hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                      >
                        <option value="all">All Models</option>
                        {[...new Set(detailData.map(d => d.model))].filter(Boolean).sort().map(m => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </select>

                      <select
                        value={detailFilters.status}
                        onChange={(e) => setDetailFilters({ ...detailFilters, status: e.target.value })}
                        className="px-2 py-1.5 text-[11px] rounded border border-slate-200 bg-white hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                      >
                        <option value="all">All Status</option>
                        <option value="success">Success</option>
                        <option value="processing">Processing</option>
                        <option value="error">Error</option>
                        <option value="failed">Failed</option>
                      </select>

                      {/* Limit Selector */}
                      <div className="flex items-center gap-2 ml-auto border-l border-slate-200 pl-3">
                        <span className="text-[11px] text-slate-500 font-medium">Show:</span>
                        <select
                          value={limit}
                          onChange={(e) => setLimit(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                          className="px-2 py-1 text-[11px] rounded border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                        >
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                          <option value="all">All</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Table COMPACT */}
                  <div className="overflow-x-auto">
                    {detailLoading ? (
                      <div className="p-8 text-center bg-white">
                        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-2" />
                        <p className="text-xs text-slate-500">Loading...</p>
                      </div>
                    ) : detailData.length === 0 ? (
                      <div className="p-12 text-center">
                        <svg className="w-12 h-12 text-slate-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                        </svg>
                        <p className="text-sm text-slate-400">No data found</p>
                      </div>
                    ) : (
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50 border-b border-slate-200">
                          <tr>
                            <th className="px-3 py-2 text-left font-semibold text-slate-600 uppercase text-[10px]">No</th>
                            <th className="px-3 py-2 text-left font-semibold text-slate-600 uppercase text-[10px]">Time</th>
                            <th className="px-3 py-2 text-left font-semibold text-slate-600 uppercase text-[10px]">User</th>
                            <th className="px-3 py-2 text-left font-semibold text-slate-600 uppercase text-[10px]">Feature</th>
                            <th className="px-3 py-2 text-left font-semibold text-slate-600 uppercase text-[10px]">Model</th>
                            <th className="px-3 py-2 text-left font-semibold text-slate-600 uppercase text-[10px]">Provider</th>
                            <th className="px-3 py-2 text-left font-semibold text-slate-600 uppercase text-[10px]">Status</th>
                            <th className="px-3 py-2 text-center font-semibold text-slate-600 uppercase text-[10px]">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detailData.map((row, idx) => (
                            <tr key={row.id} className={`border-b border-slate-100 hover:bg-blue-50 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                              {/* No Column Calculation Fix */}
                              <td className="px-3 py-1.5 text-slate-700 text-[10px] font-mono">
                                {(limit === 'all' ? 0 : (page - 1) * limit) + idx + 1}
                              </td>
                              <td className="px-3 py-1.5 text-slate-700 whitespace-nowrap">
                                {new Date(row.created_at).toLocaleString('id-ID', {
                                  day: '2-digit',
                                  month: 'short',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </td>
                              <td className="px-3 py-1.5 text-slate-700 truncate max-w-[120px]" title={row.profiles?.email}>
                                {row.profiles?.username || row.profiles?.email?.split('@')[0] || '-'}
                              </td>
                              <td className="px-3 py-1.5 text-slate-700">{row.feature_code || '-'}</td>
                              <td className="px-3 py-1.5 text-slate-700 font-mono text-[10px]">{row.model || '-'}</td>
                              <td className="px-3 py-1.5 text-slate-700">
                                <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-600 border border-blue-100">
                                  {row.provider || '-'}
                                </span>
                              </td>
                              <td className="px-3 py-1.5">
                                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border ${row.status === 'success' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' :
                                  row.status === 'processing' ? 'bg-amber-50 text-amber-600 border-amber-100' :
                                    'bg-rose-50 text-rose-600 border-rose-100'
                                  }`}>
                                  {row.status}
                                </span>
                              </td>
                              <td className="px-3 py-1.5 text-center flex items-center justify-center gap-2">
                                {/* PROCESSING: Mark as Failed */}
                                {row.status === 'processing' && (
                                  <button
                                    onClick={() => handleUpdateStatus(row.id, 'failed')}
                                    className="p-1 rounded-full bg-rose-50 text-rose-600 hover:bg-rose-100 border border-rose-200 transition-colors group relative"
                                    title="Mark as Failed"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-[10px] text-white bg-slate-800 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                                      Set Failed
                                    </span>
                                  </button>
                                )}

                                {/* FAILED/ERROR: View Detail */}
                                {(row.status === 'failed' || row.status === 'error') && (
                                  <button
                                    onClick={() => {
                                      console.log("Debug Error Row:", row);
                                      setSelectedError(row.error_message || "Data error tidak ditemukan atau belum dimuat. Silakan refresh halaman.");
                                      setShowErrorModal(true);
                                    }}
                                    className="p-1 rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200 transition-colors group relative"
                                    title="View Error Detail"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-[10px] text-white bg-slate-800 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                                      Lihat Error
                                    </span>
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* Pagination Footer */}
                  {limit !== 'all' && totalDetailCount > 0 && (
                    <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
                      <div className="text-xs text-slate-500">
                        Showing <span className="font-semibold">{((page - 1) * limit) + 1}</span> to <span className="font-semibold">{Math.min(page * limit, totalDetailCount)}</span> of <span className="font-semibold">{totalDetailCount}</span> entries
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setPage(p => Math.max(1, p - 1))}
                          disabled={page === 1}
                          className="px-2 py-1 bg-white border border-slate-300 rounded text-xs hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Prev
                        </button>
                        {[...Array(Math.min(5, Math.ceil(totalDetailCount / limit)))].map((_, i) => {
                          const totalPages = Math.ceil(totalDetailCount / limit);
                          // Determine page numbers to show (smart logic or simple?)
                          // Simple for now: just showing up to 5 surrounding pages or similar
                          // Let's just do precise logic for cleaner code
                          let pNum = i + 1;
                          // If total pages > 5, we might need sliding window. 
                          // For simplicity, let's just show current page and neighbors if feasible, or just Prev/Next with Page Indicator
                          return null;
                        })}
                        {/* Simplified Page Numbers for robustness */}
                        <div className="flex gap-1">
                          {(() => {
                            const totalPages = Math.ceil(totalDetailCount / limit);
                            const maxButtons = 5;
                            let startPage = Math.max(1, page - 2);
                            let endPage = Math.min(totalPages, startPage + maxButtons - 1);

                            if (endPage - startPage < maxButtons - 1) {
                              startPage = Math.max(1, endPage - maxButtons + 1);
                            }

                            const buttons = [];
                            for (let i = startPage; i <= endPage; i++) {
                              buttons.push(
                                <button
                                  key={i}
                                  onClick={() => setPage(i)}
                                  className={`w-6 h-6 flex items-center justify-center rounded text-xs border ${page === i
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                                    }`}
                                >
                                  {i}
                                </button>
                              );
                            }
                            return buttons;
                          })()}
                        </div>

                        <button
                          onClick={() => setPage(p => Math.min(Math.ceil(totalDetailCount / limit), p + 1))}
                          disabled={page >= Math.ceil(totalDetailCount / limit)}
                          className="px-2 py-1 bg-white border border-slate-300 rounded text-xs hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </main>
        </div>
      </div >
      {/* ERROR MODAL */}
      {showErrorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden animate-in zoom-in-95">
            <div className="bg-rose-50 px-4 py-3 border-b border-rose-100 flex justify-between items-center">
              <div className="flex items-center gap-2 text-rose-700">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <h3 className="font-bold text-sm">Error Detail</h3>
              </div>
              <button
                onClick={() => setShowErrorModal(false)}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 overflow-y-auto max-h-[300px]">
                <code className="text-xs text-slate-700 font-mono whitespace-pre-wrap break-all">
                  {(() => {
                    const text = selectedError;
                    if (!text) return "";
                    // Regex to find URLs (http/https), excluding trailing parenthesis or common punctuation if at end
                    const urlRegex = /(https?:\/\/[^\s)]+)/g;
                    const parts = text.split(urlRegex);
                    return parts.map((part, index) => {
                      if (part.match(urlRegex)) {
                        return (
                          <a
                            key={index}
                            href={part}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 underline hover:no-underline transition-colors"
                          >
                            {part}
                          </a>
                        );
                      }
                      return part;
                    });
                  })()}
                </code>
              </div>
            </div>
            <div className="bg-slate-50 px-4 py-3 border-t border-slate-100 text-right">
              <button
                onClick={() => setShowErrorModal(false)}
                className="px-4 py-2 bg-white border border-slate-200 text-slate-600 rounded-lg text-xs font-medium hover:bg-slate-50 transition-colors"
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}
    </div >
  );
};

export default DashboardAdminPage;
